    //#region Imports
    import { Component, Input, OnInit } from '@angular/core';
    import { ConfirmDialogService } from '@app/member-response-portal/service/confirm-dialog.service';
    import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
    import { Helper } from '@rawlings/core';
    import { MemberResponseHelper } from '@app/member-response-portal/shared/member-response-helper';
    import { NO_SYMBOLS_OR_SPACES_PATTERN, OTHER_HEALTH_INSURANCE_VALIDATIONS, PHONE_FORMAT, POLICYHOLDERTYPE } from '@app/member-response-portal/model/constants';
    import { SurveyOtherHealthInsuranceHelper } from '@app/member-response-portal/shared/survey-other-health-insurance-helper';
    import { ValidatorFn } from '@angular/forms';
    //#endregion Imports
    @Component({
    selector: 'app-policy-holder',
    templateUrl: './policy-holder.component.html',
    styleUrls: ['./policy-holder.component.scss']
    })
    export class PolicyHolderComponent implements OnInit {
        //#region Public Variables
        @Input() public Form! : FormGroup;
        @Input() public Index! : number;
        @Input() public PolicyHolders! : FormArray;
        public ShowPolicyHolderTypeOther: boolean = false;
        public PhoneFormat: string = PHONE_FORMAT;
        public ShowHasOtherPolicyMembers: boolean;
        public PolicyHolderTypeOtherMaxLength: number = OTHER_HEALTH_INSURANCE_VALIDATIONS.POLICY_HOLDER_OTHER_MAX_LENGTH;
        public PolicyHolderNameMaxLength: number = OTHER_HEALTH_INSURANCE_VALIDATIONS.POLICY_HOLDER_NAME_MAX_LENGTH;
        public PolicyHolderIdentificationMaxLength: number = OTHER_HEALTH_INSURANCE_VALIDATIONS.POLICY_HOLDER_IDENTIFICATION_MAX_LENGTH;
        public PolicyHolderNameOfTheCarrierMaxLength: number = OTHER_HEALTH_INSURANCE_VALIDATIONS.POLICY_HOLDER_CARRIER_NAME_MAX_LENGTH;
        //#endregion Public Variables

        //#region Protected Variables
        //#endregion Protected Variables

        //#region Private Variables
        //#endregion Private Variables

        //#region Properties
        public get PolicyMembers() : FormArray{
            return this.Form.get('PolicyMembers') as FormArray;
        }

        public get PolicyHolderType(): FormControl {
            return this.Form.get('PolicyHolderType') as FormControl;
        }

        public get HasOtherPolicyMembers(): FormControl {
            return this.Form.get('HasOtherPolicyMembers') as FormControl;
        }

        public get PolicyHolderName(): FormControl{
            return this.Form.get('PolicyHolderName') as FormControl;
        }

        public get PolicyHolderNameOfTheCarrier(): FormControl {
            return this.Form.get('PolicyHolderNameOfTheCarrier') as FormControl;
        }

        public get PolicyHolderIdentification(): FormControl {
            return this.Form.get('PolicyHolderIdentification') as FormControl;
        }

        public get PolicyHolderTypeOther(): FormControl {
            return this.Form.get('PolicyHolderOther') as FormControl;
        }

        public get ShowDeletePolicyButton(): boolean {
            return this.Index >= 1;
        }

        public get IsSectionExpanded(): boolean {
            return this.Index === this.PolicyHolders.length-1;
        }
        //#endregion Properties

        //#region Constructor
        constructor(
            private confirmDialogService: ConfirmDialogService) { }

        public ngOnInit(): void {
            const controlsToValidate: string[] = ['PolicyHolderName', 'PolicyHolderIdentification', 'PolicyHolderOther', 'PolicyHolderNameOfTheCarrier'];
            const validators : ValidatorFn = Validators.pattern(NO_SYMBOLS_OR_SPACES_PATTERN);

            controlsToValidate.forEach((control : string) => {
                this.Form.get(control).addValidators(validators);
            });

            this.SetValidatorToPolicyHolderType_Other(this.PolicyHolderType.value);

            this.PolicyHolderType.valueChanges.pipe()
            .subscribe((value: string) => {
                if (!Helper.IsEmpty(value)){
                    this.SetValues_PolicyHolderTypeOptions(value)
                    this.SetValidatorToPolicyHolderType_Other(value);
                }
            });

            const hasOtherPolicyMembersValue : boolean = MemberResponseHelper.GetBoolFromYesNoValue(this.HasOtherPolicyMembers.value);
            if(hasOtherPolicyMembersValue){
                this.HandleYesResponse_HasOtherPolicyMember();
            }

            this.HasOtherPolicyMembers.valueChanges.pipe()
            .subscribe((value: string) => {
                if (!Helper.IsEmpty(value)){
                    const hasOtherPolicyMembersValue : boolean = MemberResponseHelper.GetBoolFromYesNoValue(value);
                    if(hasOtherPolicyMembersValue){
                        this.HandleYesResponse_HasOtherPolicyMember();
                    }else{
                        this.HandleNoResponse_HasOtherPolicyMember();
                    }
                }
            });
        }
        //#endregion Constructor

        //#region Public Methods
        public AddPolicyHolderMember() : void{
            this.PolicyMembers.push(SurveyOtherHealthInsuranceHelper.CreatePolicyHolderMemberFormGroup());
        }

        public DeletePolicyHolder() : void{
            this.DeletePolicyHolderDialogConfirmChange();
        }

        public SetErrorTooltipMessage(value: string, message: string) : string {
            if (this.Form.get(value).hasError('pattern')) {
                return `Invalid ${message}`;
            }

            return null;
        }

        private ResetPolicyMembersSectionForm() : void{
            this.PolicyMembers.clear();
        }

        private SetValues_PolicyHolderTypeOptions(value : string) : void{
            this.Form.get('IsSelf').setValue(value===POLICYHOLDERTYPE.IsSelf);
            this.Form.get('IsSpouse').setValue(value===POLICYHOLDERTYPE.IsSpouse);
            this.Form.get('IsDependent').setValue(value===POLICYHOLDERTYPE.IsDependent);
            this.Form.get('IsOther').setValue(value===POLICYHOLDERTYPE.IsOther);
        }

        private HandleYesResponse_HasOtherPolicyMember() : void{
            this.VisibleSection_HasOtherPolicyMembers(true);
            if(!this.PolicyMembers.length){
                this.AddPolicyHolderMember();
            }
        }

        private HandleNoResponse_HasOtherPolicyMember() : void{
            if(this.IsDirtyPolicyMemberSectionForm()){
                this.HasOtherPolicyMembersDialogConfirmChange();
            }else{
                this.VisibleSection_HasOtherPolicyMembers(false);
                this.ResetPolicyMembersSectionForm();
            }
        }

        private HasOtherPolicyMembersDialogConfirmChange(): void {
            const onConfirmOption = (): void => {
                this.ResetPolicyMembersSectionForm();
                this.VisibleSection_HasOtherPolicyMembers(false);
            };
            const onCancelOption = (): void => {
                this.HasOtherPolicyMembers.setValue('Yes', {emitEvent:false});
                this.VisibleSection_HasOtherPolicyMembers(true);
            };

            this.confirmDialogService.OpenDialogConfirmDeleteChanges(
                onConfirmOption,
                onCancelOption
            );
        }

        private VisibleSection_HasOtherPolicyMembers(isVisible: boolean) : void{
            this.ShowHasOtherPolicyMembers =  isVisible;
        }

        private IsDirtyPolicyMemberSectionForm() : boolean {
            return this.PolicyMembers.dirty;
        }

        private SetValidatorToPolicyHolderType_Other(value : string) : void{
            this.ShowPolicyHolderTypeOther = value === POLICYHOLDERTYPE.IsOther;
            if (this.ShowPolicyHolderTypeOther) {
                this.PolicyHolderTypeOther.setValidators(Validators.required);
            } else {
                this.PolicyHolderTypeOther.clearValidators();
            }
            this.PolicyHolderTypeOther.updateValueAndValidity();
        }

        private DeletePolicyHolderDialogConfirmChange(): void {
            const onConfirmOption =  (): void => {
                this.PolicyHolders.removeAt(this.Index);
            };
            const onCancelOption = (): void => {
                //No action needed
            };

            this.confirmDialogService.OpenDialogDeleteConfirmation(
                onConfirmOption,
                onCancelOption
            );
        }
        //#endregion Public Methods
    }
